import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import { IAllergiesParams, IAllergy } from '~/interfaces'
import { fetchAllergies, message } from '~/services'
import { hasObjectValue } from '~/utils'

const UNIQUE_KEY = 'allergies'

type IUseFetchAllergies = UseQueryResult<IAllergy[], Error> & {
  getAllergies: (params: IAllergiesParams) => void
}

export const useFetchAllergies = (
  fetchParamsInitialState: IAllergiesParams | null,
  config?: UseQueryOptions<IAllergy[], Error>
): IUseFetchAllergies => {
  const [fetchParams, setFetchParams] = useState<IAllergiesParams | null>(fetchParamsInitialState)
  const hasFetchParams = hasObjectValue({ ...fetchParams })

  const fetcher = async () => {
    if (!fetchParams) throw new Error('fetchParams is required')

    try {
      const response = await fetchAllergies(fetchParams)
      const isResponseEmpty = response.length === 0

      if (isResponseEmpty) {
        // eslint-disable-next-line @typescript-eslint/no-throw-literal
        throw message('allergies.not_found')
      }

      return response
    } catch (error) {
      if (typeof error === 'string') throw new Error(error)
      throw new Error(message('messages.genericUnexpectedError'))
    }
  }

  const getAllergies = useCallback((params: IAllergiesParams) => {
    setFetchParams(params)
  }, [])

  return {
    ...useQuery<IAllergy[], Error>([UNIQUE_KEY, fetchParams?.patientId], fetcher, {
      cacheTime: Infinity,
      staleTime: Infinity,
      enabled: hasFetchParams,
      ...config
    }),
    getAllergies
  }
}
